import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@astro/auth'
import { APP_URLS } from '@astro/config'
import AuthScene, { ReturnHomeLink } from '../components/auth/AuthScene'
import LoginForm from '../components/auth/LoginForm'
import RegisterForm from '../components/auth/RegisterForm'
import OtpLoginForm from '../components/auth/OtpLoginForm'
import ForgotPasswordForm from '../components/auth/ForgotPasswordForm'

/** Deep-linkable views — `/login?mode=forgot` opens the reset-request form. */
const MODES = ['login', 'register', 'otp', 'forgot']

/**
 * Auth page: one view at a time inside the shared AuthScene shell. Only the
 * active view (login / register / otp / forgot) is mounted; each view owns its
 * form logic and its own External Path Sync handlers.
 */
export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { status } = useAuth()

  const [mode, setMode] = useState(() => {
    const requested = searchParams.get('mode')
    return MODES.includes(requested) ? requested : 'login'
  })
  // Set when registration succeeds but the API wants an explicit sign-in.
  const [loginPrefillEmail, setLoginPrefillEmail] = useState('')
  // Carries whatever was typed in the login form into the reset-request form.
  const [forgotPrefillEmail, setForgotPrefillEmail] = useState('')

  // Where to land after a successful sign-in. `?redirect=` may point at a
  // sibling app (shop, education) — only trusted origins are honoured.
  const followRedirect = () => {
    const target = searchParams.get('redirect')
    if (target) {
      if (target.startsWith('/')) return navigate(target, { replace: true })
      try {
        const targetOrigin = new URL(target)
        // Sibling apps share the parent-domain SSO cookie, so any subdomain in
        // production is trusted. In local dev every app runs on `localhost` at
        // a different port (shop :5175, website :5173, …) — APP_URLS only has
        // the production hostnames, so a same-hostname match is what actually
        // lets a dev redirect back to the app that triggered the login.
        const sameHost = targetOrigin.hostname === window.location.hostname
        const trustedProdApp = Object.values(APP_URLS).some(
          (o) => target === o || target.startsWith(`${o}/`),
        )
        if (sameHost || trustedProdApp) return window.location.assign(target)
      } catch {
        // malformed redirect param — fall through to the default below
      }
    }
    navigate(location.state?.from?.pathname ?? '/', { replace: true })
  }

  // Fires on mount for already-signed-in visitors (deep link back here) and
  // after any view's sign-in succeeds — the views only toast; this redirects.
  useEffect(() => {
    if (status === 'authenticated') followRedirect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  // Both switchers are handed straight to onClick/onBack props, so a handler
  // wired as `onClick={switchToLogin}` passes React's click event — the string
  // check keeps that from landing in an input as "[object Object]".
  const switchToLogin = (prefillEmail) => {
    if (typeof prefillEmail === 'string' && prefillEmail) setLoginPrefillEmail(prefillEmail)
    setMode('login')
  }

  const switchToForgot = (typedEmail) => {
    setForgotPrefillEmail(typeof typedEmail === 'string' ? typedEmail : '')
    setMode('forgot')
  }

  return (
    <AuthScene>
      {mode === 'login' && (
        <LoginForm
          prefillEmail={loginPrefillEmail}
          onSwitchToRegister={() => setMode('register')}
          onSwitchToOtp={() => setMode('otp')}
          onForgotPassword={switchToForgot}
        />
      )}
      {mode === 'register' && <RegisterForm onSwitchToLogin={switchToLogin} />}
      {mode === 'otp' && <OtpLoginForm onBackToLogin={() => setMode('login')} />}
      {mode === 'forgot' && (
        <ForgotPasswordForm prefillEmail={forgotPrefillEmail} onBackToLogin={switchToLogin} />
      )}

      {mode !== 'otp' && <ReturnHomeLink />}
    </AuthScene>
  )
}
